import { batch, useSignal } from '@preact/signals'
import { Text } from 'preact-i18n'
import { Suspense, lazy } from 'preact/compat'
import type { StepTour } from '../molecules/Tour'

import { ScannerMode } from '../../store/InitialOptionsStore'
import { CookieManager } from '../../utils'

const Tour = lazy(() => import('../molecules/Tour'))
const TutorialModal = lazy(() => import('../molecules/TutorialModal'))

const barcodeTutorialSteps: StepTour[] = [
  {
    selector: '#settings',
    content: <Text id="tutorial.steps.step1" />,
    position: 'bottom left',
  },
  {
    selector: '#camera',
    content: <Text id="tutorial.steps.step2" />,
    position: 'bottom left',
  },
  {
    selector: '#soundEffects',
    content: <Text id="tutorial.steps.step3" />,
    position: 'bottom center',
  },
  {
    selector: '#duplicateFilter',
    content: <Text id="tutorial.steps.step4" />,
    position: 'bottom right',
  },
  {
    selector: '#itemsList',
    content: <Text id="tutorial.steps.step5" />,
    position: 'top center',
    basic: true,
  },
]

const batchTutorialSteps: StepTour[] = [
  {
    selector: '#settings',
    content: <Text id="tutorial.steps.step1" />,
    position: 'bottom left',
  },
  {
    selector: '#camera',
    content: <Text id="tutorial.steps.step2" />,
    position: 'bottom left',
  },
]

const labelCaptureTutorialSteps: StepTour[] = [
  {
    selector: '#settings',
    content: <Text id="tutorial.steps.labelStep1" />,
    position: 'bottom left',
  },
  {
    selector: '#camera',
    content: <Text id="tutorial.steps.step2" />,
    position: 'bottom left',
  },
]

function getTutorialSteps(scannerMode: ScannerMode | undefined): StepTour[] {
  switch (scannerMode) {
    case ScannerMode.LabelScanning:
      return labelCaptureTutorialSteps
    case ScannerMode.Batch:
      return batchTutorialSteps
    default:
      return barcodeTutorialSteps
  }
}

const COOKIE_NAME = 'tutorial_seen'
const noop = () => void 0

type TutorialProps = {
  scannerMode?: ScannerMode
  onNextTip?: () => void
  onConfirmAction?: () => void
  onFinish?: () => void
}

const Tutorial = ({ scannerMode, onNextTip = noop, onConfirmAction = noop, onFinish = noop }: TutorialProps) => {
  const steps = getTutorialSteps(scannerMode)
  const finished = useSignal(false)
  const stepIndex = useSignal(-1)
  const tutorialModalOpen = useSignal(CookieManager.get(COOKIE_NAME) !== 'y')

  return CookieManager.get(COOKIE_NAME) !== 'y' ? (
    <Suspense fallback={null}>
      <TutorialModal
        tutorialFinished={finished.value || stepIndex.value >= steps.length - 1}
        open={tutorialModalOpen.value}
        onNextTip={() => {
          stepIndex.value += 1
          onNextTip()
        }}
        onConfirmAction={() => {
          batch(() => {
            stepIndex.value = -1
            tutorialModalOpen.value = false
            CookieManager.set(COOKIE_NAME, 'y', 365)
            onConfirmAction()
          })
        }}
      />
      <Tour
        steps={steps}
        stepIndex={stepIndex.value}
        onFinish={() => {
          stepIndex.value = -1
          finished.value = true
          onFinish()
        }}
      />
    </Suspense>
  ) : null
}

export default Tutorial
